// src/pages/AddLog.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import logService from '../services/logService';
import ingredientService from '../services/ingredientService';
import ingredientInventoryService from '../services/ingredientInventoryService';
import productService from '../services/productService';
import bakingCcpService from '../services/bakingCcpService';
import { usePermissions } from '../context/PermissionContext';
import { generateLotCode } from '../utils/lotCodeUtils';

function AddLog() {
  const { companySettings } = usePermissions();
  const requireQty = companySettings.requireIngredientQty !== false; // default true
  const deviationThreshold = companySettings.ingredientDeviationThreshold ?? 5;
  const lotCodeFormat = companySettings.lotCodeFormat || null;

  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success'); // 'success' | 'danger'
  const [lotCode, setLotCode] = useState('');
  const [productionDate, setProductionDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [entries, setEntries] = useState([
    { productId: '', productName: '', quantity: '' }
  ]);
  const [productSuggestions, setProductSuggestions] = useState({});
  const [ingredientEntries, setIngredientEntries] = useState([
    { ingredientId: '', ingredientLotCode: '', quantity: '', uom: 'lb', expectedQtyKg: null, deviationJustification: '' }
  ]);
  // { [ingredientId]: [{ lotCode, remainingKg }] }
  const [availableLotCodes, setAvailableLotCodes] = useState({});
  const [lotCodeExists, setLotCodeExists] = useState(false);
  const lotCodeCheckRef = useRef(null);

  // QA banner state
  const [pendingRuns, setPendingRuns] = useState([]);
  const [canReviewQA, setCanReviewQA] = useState(false);

  const cachedRecipesRef = useRef({});
  const fetchedLotCodeIdsRef = useRef(new Set());

  useEffect(() => {
    // Determine role from token
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwtDecode(token);
        const role = decoded?.role || '';
        setCanReviewQA(role === 'admin' || role === 'qa');
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Fetch pending QA runs if user has permission
  useEffect(() => {
    if (!canReviewQA) return;
    bakingCcpService.getRuns('COMPLETED')
      .then(res => setPendingRuns(Array.isArray(res?.runs) ? res.runs : []))
      .catch(() => setPendingRuns([]));
  }, [canReviewQA]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const productsData = await logService.getProducts();
        setProducts(productsData);

        const ingredientsData = await ingredientService.getIngredients();
        setIngredients(ingredientsData);

        const initialCode = generateLotCode(lotCodeFormat);
        setLotCode(initialCode);
        checkLotCodeExists(initialCode);
      } catch (error) {
        console.error('Error loading data:', error);
        showMessage('Error loading data.', 'danger');
      }
    };
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateIngredientEntries = async () => {
      const allIngredientIds = new Set();
      for (let entry of entries) {
        if (!entry.productId) continue;
        let recipe = cachedRecipesRef.current[entry.productId];
        if (!recipe) {
          try {
            const raw = await productService.getProductRecipe(entry.productId);
            recipe = raw.allIngredients || (Array.isArray(raw) ? raw : (raw.ingredients || []));
            cachedRecipesRef.current[entry.productId] = recipe;
          } catch (e) {
            console.error('Error fetching recipe for', entry.productId, e);
            continue;
          }
        }
        recipe.forEach(item => allIngredientIds.add(item.ingredientId));
      }
      // Build a lookup: ingredientId -> total expectedQuantityKg (recipe per unit × units produced)
      const expectedQtyMap = {};
      for (const entry of entries) {
        if (!entry.productId) continue;
        const qty = Number(entry.quantity) || 0;
        if (!qty) continue;
        const recipe = cachedRecipesRef.current[entry.productId] || [];
        for (const item of recipe) {
          if (item.expectedQuantityKg != null) {
            expectedQtyMap[item.ingredientId] = (expectedQtyMap[item.ingredientId] || 0) + Number(item.expectedQuantityKg) * qty;
          }
        }
      }
      const newIng = Array.from(allIngredientIds).map(id => ({
        ingredientId: id,
        ingredientLotCode: '',
        quantity: '',
        uom: 'lb',
        expectedQtyKg: expectedQtyMap[id] ?? null,
        deviationJustification: '',
      }));
      setIngredientEntries(newIng);

      // Fetch available lot codes for all ingredients in the recipe
      const idsToFetch = Array.from(allIngredientIds).filter(id => !fetchedLotCodeIdsRef.current.has(id));
      if (idsToFetch.length > 0) {
        const fetched = await Promise.all(
          idsToFetch.map(id =>
            ingredientInventoryService.getLotCodesAvailable(id)
              .then(lcs => [id, lcs])
              .catch(() => [id, []])
          )
        );
        fetched.forEach(([id]) => fetchedLotCodeIdsRef.current.add(id));
        setAvailableLotCodes(prev => ({ ...prev, ...Object.fromEntries(fetched) }));
      }
    };
    updateIngredientEntries();
  }, [entries]);

  const checkLotCodeExists = (code) => {
    clearTimeout(lotCodeCheckRef.current);
    if (!code) { setLotCodeExists(false); return; }
    lotCodeCheckRef.current = setTimeout(async () => {
      try {
        const { exists } = await logService.checkLotCode(code);
        setLotCodeExists(exists);
      } catch { /* ignore */ }
    }, 400);
  };


  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3500);
  };

  const toKg = (qty, uom) => {
    const n = Number(qty);
    if (!Number.isFinite(n)) return null;
    if (uom === 'kg') return n;
    if (uom === 'lb') return n * 0.45359237;
    return null;
  };

  const handleProductInputChange = (idx, value) => {
    const clone = [...entries];
    clone[idx].productName = value;
    clone[idx].productId = '';
    setEntries(clone);
    if (value.length > 0) {
      const suggestions = products.filter(p =>
        p.name.toLowerCase().includes(value.toLowerCase())
      );
      setProductSuggestions(ps => ({ ...ps, [idx]: suggestions }));
    } else {
      setProductSuggestions(ps => ({ ...ps, [idx]: [] }));
    }
  };

  const handleSelectProduct = (idx, product) => {
    const clone = [...entries];
    clone[idx].productName = product.name;
    clone[idx].productId = product.id;
    setEntries(clone);
    setProductSuggestions(ps => ({ ...ps, [idx]: [] }));
  };

  const handleShowAllProducts = (idx) => {
    setProductSuggestions(ps => ({ ...ps, [idx]: products }));
  };

  const handleEntryChange = (idx, field, value) => {
    const clone = [...entries];
    clone[idx][field] = value;
    setEntries(clone);
  };
  const addEntry = () =>
    setEntries(e => [...e, { productId: '', productName: '', quantity: '' }]);
  const removeEntry = idx =>
    setEntries(e => e.filter((_, i) => i !== idx));

  const handleIngredientChange = (idx, field, value) => {
    const clone = [...ingredientEntries];
    clone[idx][field] = value;
    setIngredientEntries(clone);
    if (field === 'ingredientId' && value && !availableLotCodes[value]) {
      ingredientInventoryService.getLotCodesAvailable(value)
        .then(lcs => setAvailableLotCodes(prev => ({ ...prev, [value]: lcs })))
        .catch(() => {});
    }
  };
  const addIngredientEntry = () =>
    setIngredientEntries(i => [...i, { ingredientId: '', ingredientLotCode: '', quantity: '', uom: 'lb', expectedQtyKg: null, deviationJustification: '' }]);
  const removeIngredientEntry = idx =>
    setIngredientEntries(i => i.filter((_, i2) => i2 !== idx));

  const handleSubmit = async e => {
    e.preventDefault();

    if (!productionDate) {
      return showMessage('Please select a production date.', 'danger');
    }
    if (entries.some(en => !en.productId || !en.quantity)) {
      return showMessage('Please fill out all product fields.', 'danger');
    }
    if (
      ingredientEntries.some(en =>
        !en.ingredientId ||
        !en.ingredientLotCode ||
        (requireQty && (en.quantity === '' || !Number.isFinite(Number(en.quantity)) || Number(en.quantity) <= 0))
      )
    ) {
      return showMessage(
        requireQty
          ? 'Please fill out all ingredient fields (including quantity).'
          : 'Please fill out ingredient ID and lot code for all ingredients.',
        'danger'
      );
    }

    if (lotCodeExists) {
      return showMessage(
        `Lot code "${lotCode}" already has production logs. Change the lot code to start a new batch.`,
        'danger'
      );
    }

    // Check justification is present for any deviation
    for (const en of ingredientEntries) {
      const uom = (en.uom || 'lb').toLowerCase();
      const qKg = en.quantity !== '' ? toKg(en.quantity, uom) : null;
      if (qKg != null && en.expectedQtyKg != null && en.expectedQtyKg > 0) {
        const devPct = Math.abs(((qKg - en.expectedQtyKg) / en.expectedQtyKg) * 100);
        if (devPct > deviationThreshold && !en.deviationJustification?.trim()) {
          return showMessage('A justification is required for all ingredients with quantity deviations.', 'danger');
        }
      }
    }

    try {
      const ingredientPayload = ingredientEntries.map(en => {
        const uom = (en.uom || 'lb').toLowerCase();
        const qKg = en.quantity !== '' ? toKg(en.quantity, uom) : null;
        if (requireQty && (!Number.isFinite(qKg) || qKg <= 0)) {
          throw new Error('Invalid ingredient quantity/unit. Please check quantities and units.');
        }
        let deviationPct = null;
        if (qKg != null && en.expectedQtyKg != null && en.expectedQtyKg > 0) {
          deviationPct = ((qKg - en.expectedQtyKg) / en.expectedQtyKg) * 100;
        }
        return {
          ingredientId: Number(en.ingredientId),
          ingredientLotCode: String(en.ingredientLotCode || '').trim(),
          quantityKg: qKg,
          quantityInput: en.quantity !== '' ? Number(en.quantity) : null,
          uomInput: uom,
          deviationPct,
          deviationJustification: en.deviationJustification || null,
        };
      });

      await logService.addBatchLogs({
        entries: entries.map(en => ({ ...en, lotCode })),
        ingredientEntries: ingredientPayload,
        lotCode,
        production_date: productionDate,
      });

      showMessage('Production batch submitted successfully.', 'success');

      setEntries([{ productId: '', productName: '', quantity: '' }]);
      setIngredientEntries([{ ingredientId: '', ingredientLotCode: '', quantity: '', uom: 'lb', expectedQtyKg: null, deviationJustification: '' }]);
      const newCode = generateLotCode(lotCodeFormat);
      setLotCode(newCode);
      setLotCodeExists(false);
      setProductionDate(new Date().toISOString().slice(0, 10));
    } catch (err) {
      console.error(err);
      showMessage(err?.message || 'Error submitting batch logs.', 'danger');
    }
  };

  return (
    <div className="container mt-4">

      {/* ── Floating message banner ── */}
      {message && (
        <div className="fixed-top mt-5 d-flex justify-content-center" style={{ zIndex: 1100 }}>
          <div className={`alert alert-${messageType} alert-dismissible fade show w-50`} role="alert">
            {message}
            <button type="button" className="btn-close" onClick={() => setMessage('')} />
          </div>
        </div>
      )}

      {/* ── QA Pending Review Banner ── */}
      {canReviewQA && pendingRuns.length > 0 && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 mb-4" role="alert">
          <div>
            <strong>⚠ {pendingRuns.length} run{pendingRuns.length > 1 ? 's' : ''} pending QA verification.</strong>
            <div style={{ fontSize: 13 }} className="mt-1">
              The following batches were completed by the production team and are waiting for QA review:{' '}
              {pendingRuns.slice(0, 3).map((r, i) => (
                <span key={r.id}>
                  <strong>{r.Batch?.lotCode || `Run #${r.id}`}</strong>
                  {i < Math.min(pendingRuns.length, 3) - 1 ? ', ' : ''}
                </span>
              ))}
              {pendingRuns.length > 3 && <span> and {pendingRuns.length - 3} more</span>}
            </div>
          </div>
          <Link
            to="/ccp/baking/queue"
            className="btn btn-warning text-dark flex-shrink-0"
            style={{ fontWeight: 700, whiteSpace: 'nowrap' }}
          >
            Go to QA Queue →
          </Link>
        </div>
      )}

      <h2>Add Production Batch</h2>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Use this form to manually log a production batch. If you completed a run through the live baking process, use the{' '}
        <Link to="/ccp/baking/queue">QA Review Queue</Link> instead.
      </p>

      <form onSubmit={handleSubmit}>
        <h4>Batch Lot & Date</h4>
        <div className="row mb-3">
          <div className="col-md-6 col-12 mb-2 mb-md-0">
            <label>Lot Code</label>
            <input
              type="text"
              className={`form-control ${lotCodeExists ? 'border-danger' : ''}`}
              value={lotCode}
              onChange={e => { setLotCode(e.target.value); checkLotCodeExists(e.target.value); }}
              required
            />
            {lotCodeExists && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                ⚠ This lot code already has production logs. Change it to start a new batch.
              </div>
            )}
          </div>
          <div className="col-md-6 col-12">
            <label>Production Date</label>
            <input
              type="date"
              className="form-control"
              value={productionDate}
              onChange={e => setProductionDate(e.target.value)}
              required
            />
          </div>
        </div>

        <h4>Products</h4>
        {entries.map((entry, idx) => (
          <div key={idx} className="row mb-3 g-1">
            <div className="col-sm-5 col-12 position-relative">
              <label>Product</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={entry.productName}
                  onChange={e => handleProductInputChange(idx, e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => handleShowAllProducts(idx)}
                >
                  ▾
                </button>
              </div>
              {productSuggestions[idx]?.length > 0 && (
                <ul
                  className="list-group position-absolute w-100 mt-1"
                  style={{ maxHeight: '250px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  {productSuggestions[idx].map(p => (
                    <li
                      key={p.id}
                      className="list-group-item"
                      style={{ cursor: 'pointer' }}
                      onMouseDown={() => handleSelectProduct(idx, p)}
                    >
                      {p.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="col-sm-5 col-12">
              <label>Quantity</label>
              <input
                type="number"
                className="form-control"
                value={entry.quantity}
                onChange={e => handleEntryChange(idx, 'quantity', e.target.value)}
                required
              />
            </div>
            <div className="col-sm-2 col-12 d-flex align-items-end">
              <button type="button" className="btn btn-danger w-100" onClick={() => removeEntry(idx)}>
                Remove
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="btn btn-secondary mb-3" onClick={addEntry}>
          Add Product
        </button>

        <h4>Ingredients</h4>
        {ingredientEntries.map((ingredient, idx) => {
          const enteredKg = ingredient.quantity !== '' ? toKg(ingredient.quantity, ingredient.uom || 'lb') : null;
          const expectedKg = ingredient.expectedQtyKg;
          let deviationPct = null;
          if (enteredKg != null && expectedKg != null && expectedKg > 0) {
            deviationPct = ((enteredKg - expectedKg) / expectedKg) * 100;
          }
          const hasDeviation = deviationPct !== null && Math.abs(deviationPct) > deviationThreshold;

          return (
          <div key={idx} className="mb-3">
            <div className="row g-1">
              <div className="col-md-3 col-12">
                <label>Ingredient</label>
                <select
                  className="form-control"
                  value={ingredient.ingredientId}
                  onChange={e => handleIngredientChange(idx, 'ingredientId', e.target.value)}
                  required
                >
                  <option value="">Select an ingredient</option>
                  {ingredients.map(ing => (
                    <option key={ing.id} value={ing.id}>{ing.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-2 col-12">
                <label>Lot Code</label>
                {(() => {
                  const datalistId = `lc-list-${idx}`;
                  const available = availableLotCodes[ingredient.ingredientId] || [];
                  const enteredLc = (ingredient.ingredientLotCode || '').trim().toUpperCase();
                  const notInInventory = enteredLc && available.length > 0 &&
                    !available.some(a => a.lotCode.toUpperCase() === enteredLc);
                  return (
                    <>
                      <datalist id={datalistId}>
                        {available.map(a => (
                          <option key={a.lotCode} value={a.lotCode}>
                            {a.lotCode} — {Number(a.remainingKg).toFixed(1)} kg remaining
                          </option>
                        ))}
                      </datalist>
                      <input
                        type="text"
                        list={datalistId}
                        className="form-control"
                        value={ingredient.ingredientLotCode}
                        onChange={e => handleIngredientChange(idx, 'ingredientLotCode', e.target.value)}
                        placeholder="Enter or select lot code"
                        required
                      />
                      {notInInventory && (
                        <div className="text-warning mt-1" style={{ fontSize: 11 }}>
                          ⚠ Lot code not found in inventory
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="col-md-2 col-12">
                <label>
                  Quantity{!requireQty && <span className="text-muted ms-1" style={{ fontSize: 12 }}>(optional)</span>}
                </label>
                <input
                  type="number"
                  className={`form-control ${hasDeviation ? 'border-warning' : ''}`}
                  value={ingredient.quantity}
                  onChange={e => handleIngredientChange(idx, 'quantity', e.target.value)}
                  min="0"
                  step="0.01"
                  required={requireQty}
                />
              </div>

              <div className="col-md-1 col-6">
                <label>Unit</label>
                <select
                  className="form-control"
                  value={ingredient.uom}
                  onChange={e => handleIngredientChange(idx, 'uom', e.target.value)}
                >
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                </select>
              </div>

              <div className="col-md-2 col-12">
                <label className="text-muted" style={{ fontSize: 12 }}>Expected (kg)</label>
                <div className="form-control-plaintext ps-1" style={{ fontSize: 13, paddingTop: '0.5rem' }}>
                  {expectedKg != null
                    ? <>
                        <span>{Number(expectedKg).toFixed(3)} kg</span>
                        {deviationPct !== null && (
                          <span
                            className={`ms-2 badge ${hasDeviation ? 'bg-warning text-dark' : 'bg-success'}`}
                            style={{ fontSize: 11 }}
                          >
                            {deviationPct > 0 ? '+' : ''}{deviationPct.toFixed(1)}%
                          </span>
                        )}
                      </>
                    : <span className="text-muted">—</span>
                  }
                </div>
              </div>

              <div className="col-md-2 col-6 d-flex align-items-end">
                <button type="button" className="btn btn-danger w-100" onClick={() => removeIngredientEntry(idx)}>
                  Remove
                </button>
              </div>
            </div>

            {hasDeviation && (
              <div className="row g-1 mt-1">
                <div className="col-12">
                  <div className="alert alert-warning py-2 px-3 mb-0" style={{ fontSize: 13 }}>
                    <strong>⚠ Quantity deviation of {deviationPct.toFixed(1)}% detected</strong> (threshold: {deviationThreshold}%).
                    Please enter a justification:
                    <textarea
                      className="form-control form-control-sm mt-2"
                      rows={2}
                      placeholder="Explain why the quantity differs from the recipe…"
                      value={ingredient.deviationJustification || ''}
                      onChange={e => handleIngredientChange(idx, 'deviationJustification', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })}

        <div className="d-flex justify-content-center gap-3 mb-5">
          <button type="button" className="btn btn-secondary" onClick={addIngredientEntry}>
            Add Ingredient
          </button>
          <button type="submit" className="btn btn-primary">
            Submit Production Batch
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddLog;